import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  InputAdornment, Chip, Grid, Drawer, Collapse, Tooltip as MuiTooltip,
  Checkbox, FormControlLabel, TableSortLabel, Select, MenuItem, FormControl
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LinkIcon from "@mui/icons-material/Link";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import {
  doc, addDoc, getDocs, onSnapshot, updateDoc, arrayUnion, arrayRemove,
  collection, writeBatch, serverTimestamp
} from "firebase/firestore";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from "recharts";
import { useLista } from "../../../hooks/useListe";
import { useKpiCorsoData } from "../../../hooks/useKpiCorsoData";
import { useAuth } from "../../../context/AuthContext";
import { salvaLog } from "../../../utils/logger";
import ReferentiCorso from "../../../components/ReferentiCorso";
import {
  leggiIscrittiXlsx, calcolaKpiStudenti, calcolaKpiCorso, costruisciRigheQuiz,
  LABEL_TREND, esportaKpiXlsx, statoStudente, CAMPI_EXTRA_ISCRITTO, separaCodiciFiscali, unisciIscritti
} from "../../../utils/kpiCorso";
import { salvaCodiciFiscali, ascoltaCodiciFiscali } from "../../../utils/anagraficaRiservata";
import { secondiInHHMMSS } from "../../../utils/parseReportPresenze";

function leggiJson(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dati = JSON.parse(e.target.result);
        resolve(Array.isArray(dati) ? dati : []);
      } catch (err) {
        reject(new Error("Il file JSON non è valido"));
      }
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function TrendIcon({ trend }) {
  if (trend === "migliorato") return <TrendingUpIcon fontSize="small" color="success" />;
  if (trend === "peggiorato") return <TrendingDownIcon fontSize="small" color="error" />;
  if (trend === "stabile") return <TrendingFlatIcon fontSize="small" color="disabled" />;
  return <HelpOutlineIcon fontSize="small" color="disabled" />;
}

function arrotonda(v) {
  return v == null ? "—" : `${Math.round(v * 10) / 10}%`;
}

// Costanza espressa come "lezioni frequentate / lezioni svolte finora" oltre alla percentuale
function formatCostanza(s) {
  if (!s.lezioniTotali) return "—";
  return `${s.lezioniFrequentate}/${s.lezioniTotali} (${arrotonda(s.costanzaPresenza)})`;
}

// Quiz svolti espressi come "quiz svolti / quiz disponibili nel corso", stesso formato della costanza
function formatQuizSvolti(s) {
  if (!s.quizTotali) return "—";
  return `${s.quizSvolti}/${s.quizTotali}`;
}

const OGGI = () => new Date().toISOString().slice(0, 10);

export default function CorsoDetail() {
  const { corsoId } = useParams();
  const navigate = useNavigate();
  const { items: clienti } = useLista("clienti");

  const [corso, setCorso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [codiciFiscali, setCodiciFiscali] = useState({}); // email -> CF, da corsi/{id}/riservato

  // --- Caricamento corso (realtime) ---
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "corsi", corsoId), (snap) => {
      if (snap.exists()) setCorso({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
    return unsub;
  }, [corsoId]);

  useEffect(() => ascoltaCodiciFiscali(corsoId, setCodiciFiscali), [corsoId]);

  const { eventi, quizIndipendenti, presenze, risultatiQuiz, loading: loadingLezioni } = useKpiCorsoData(corsoId, corso?.eventiIds);
  const eventiReali = eventi.filter((e) => !e.standalone);

  const clientiMap = Object.fromEntries(clienti.map((c) => [c.id, c.nome]));
  const nomeCliente = clientiMap[corso?.clienteId] || "—";

  const kpiStudenti = useMemo(
    () => calcolaKpiStudenti(eventi, presenze, risultatiQuiz, corso?.iscritti || []),
    [eventi, presenze, risultatiQuiz, corso]
  );
  const kpiCorsoSerie = useMemo(
    () => calcolaKpiCorso(eventi, presenze, risultatiQuiz, corso?.iscritti || []),
    [eventi, presenze, risultatiQuiz, corso]
  );

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (!corso) return <Alert severity="error" sx={{ m: 4 }}>Corso non trovato.</Alert>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/utility/kpi-studenti")}
          variant="outlined" size="small">
          Corsi
        </Button>
        <Box flexGrow={1}>
          <Typography variant="h5" fontWeight="bold">{corso.nome}</Typography>
          <Typography variant="body2" color="text.secondary">{nomeCliente}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />}
          onClick={() => esportaKpiXlsx(corso, kpiStudenti, { codiciFiscali })}>
          Esporta XLSX
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Iscritti (${(corso.iscritti || []).length})`} />
        <Tab label={`Lezioni (${eventiReali.length})`} />
        <Tab label="KPI Studenti" />
        <Tab label="Referenti" />
      </Tabs>

      {tab === 0 && <TabIscritti corso={corso} codiciFiscali={codiciFiscali} />}
      {tab === 1 && (
        <TabLezioni corso={corso} clientiMap={clientiMap} eventi={eventiReali} quizIndipendenti={quizIndipendenti}
          presenze={presenze} risultatiQuiz={risultatiQuiz} loading={loadingLezioni} />
      )}
      {tab === 2 && <TabKpi kpiStudenti={kpiStudenti} kpiCorsoSerie={kpiCorsoSerie} />}
      {tab === 3 && <TabReferenti corsoId={corsoId} />}
    </Box>
  );
}

// ============================= TAB ISCRITTI =============================

function TabIscritti({ corso, codiciFiscali }) {
  const { currentUser } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null | { index, ...campi } per nuovo/modifica
  const [ricerca, setRicerca] = useState("");
  const [errore, setErrore] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [esitoImport, setEsitoImport] = useState(null); // { severity, testo }

  async function salvaIscritti(nuovoArray) {
    await updateDoc(doc(db, "corsi", corso.id), { iscritti: nuovoArray });
  }

  function apriNuovo() {
    const extra = Object.fromEntries(CAMPI_EXTRA_ISCRITTO.map((c) => [c.key, ""]));
    setEditing({
      index: -1, firstName: "", lastName: "", email: "", ...extra, codiceFiscale: "",
      dataIscrizione: OGGI(), dataRimozione: "",
    });
    setErrore(null);
    setDialogOpen(true);
  }

  function apriModifica(index) {
    const iscritto = corso.iscritti[index];
    const extra = Object.fromEntries(CAMPI_EXTRA_ISCRITTO.map((c) => [c.key, iscritto[c.key] || ""]));
    setEditing({
      index, ...iscritto, ...extra,
      codiceFiscale: codiciFiscali[iscritto.email?.toLowerCase()] || "",
      dataRimozione: iscritto.dataRimozione || "",
    });
    setErrore(null);
    setDialogOpen(true);
  }

  async function salvaForm() {
    if (!editing.firstName.trim() || !editing.email.trim()) return;
    const email = editing.email.trim().toLowerCase();
    const attuale = corso.iscritti || [];
    const duplicato = attuale.some((i, idx) => idx !== editing.index && i.email?.toLowerCase() === email);
    if (duplicato) { setErrore("Esiste già un iscritto con questa email."); return; }
    setSalvando(true);
    const nuovoIscritto = editing.index === -1;
    const { index, codiceFiscale, ...campi } = editing;
    const nuovo = {
      ...campi,
      ...Object.fromEntries(CAMPI_EXTRA_ISCRITTO.map((c) => [c.key, String(editing[c.key] || "").trim()])),
      firstName: editing.firstName.trim(),
      lastName: editing.lastName.trim(),
      email,
      // un iscritto importato ha dataIscrizione null ("da inizio corso"): modificarlo non deve
      // trasformarla in oggi, altrimenti sparirebbero dai KPI le lezioni già svolte
      dataIscrizione: editing.dataIscrizione || (nuovoIscritto ? OGGI() : null),
      dataRimozione: editing.dataRimozione || null,
    };
    const arr = [...attuale];
    if (nuovoIscritto) arr.push(nuovo); else arr[index] = nuovo;
    await salvaIscritti(arr);
    const cf = String(codiceFiscale || "").trim().toUpperCase();
    if (cf && cf !== codiciFiscali[email]) await salvaCodiciFiscali(corso.id, { [email]: cf });
    salvaLog(currentUser, nuovoIscritto ? "Aggiunta iscritto corso" : "Modifica iscritto corso", corso.id, corso.nome, [
      { campo: "Iscritto", da: "—", a: `${nuovo.firstName} ${nuovo.lastName} (${nuovo.email})` }
    ]);
    setSalvando(false);
    setDialogOpen(false);
  }

  async function rimuoviRapido(index) {
    const arr = [...(corso.iscritti || [])];
    const iscritto = arr[index];
    arr[index] = { ...iscritto, dataRimozione: OGGI() };
    await salvaIscritti(arr);
    salvaLog(currentUser, "Rimozione iscritto corso", corso.id, corso.nome, [
      { campo: "Iscritto", da: `${iscritto.firstName} ${iscritto.lastName} (${iscritto.email})`, a: "Uscito" }
    ]);
  }

  async function handleImportaXlsx(e) {
    const file = e.target.files[0];
    if (!file) return;
    setEsitoImport(null);
    try {
      const { righe, codiciFiscali: cfImportati } = separaCodiciFiscali(await leggiIscrittiXlsx(file));
      // Nuovi iscritti con dataIscrizione null = nessun limite inferiore sulle lezioni conteggiate
      // nei KPI (es. dimenticati nel roster iniziale); per un ingresso davvero a metà corso, la
      // data va poi impostata a mano riga per riga con l'icona di modifica.
      const { iscritti: unione, aggiunti, aggiornati, assenti } = unisciIscritti(corso.iscritti, righe);
      if (aggiunti > 0 || aggiornati > 0) await salvaIscritti(unione);
      await salvaCodiciFiscali(corso.id, cfImportati);
      salvaLog(currentUser, "Import iscritti corso", corso.id, corso.nome, [
        { campo: "Aggiunti", da: "—", a: String(aggiunti) },
        { campo: "Aggiornati", da: "—", a: String(aggiornati) },
      ]);
      const testoAssenti = assenti.length
        ? ` ${assenti.length} iscritti attivi non sono nel file e sono rimasti invariati: ` +
          assenti.map((i) => `${i.firstName} ${i.lastName}`.trim() || i.email).join(", ") + "."
        : "";
      setEsitoImport({
        severity: assenti.length ? "warning" : "success",
        testo: `Import completato: ${righe.length} righe lette, ${aggiunti} nuovi iscritti, ${aggiornati} aggiornati.${testoAssenti}`,
      });
    } catch (err) {
      console.error("Errore import iscritti:", err);
      setEsitoImport({ severity: "error", testo: `Errore durante l'import: ${err.message}` });
    }
    e.target.value = "";
  }

  const iscritti = corso.iscritti || [];
  // Le colonne anagrafiche compaiono solo se almeno un iscritto le ha valorizzate,
  // così i corsi importati col vecchio file (solo nome/cognome/email) restano compatti
  const colonneExtra = CAMPI_EXTRA_ISCRITTO.filter((c) => iscritti.some((i) => i[c.key]));
  const mostraCf = Object.keys(codiciFiscali).length > 0;
  const filtrati = iscritti
    .map((i, index) => ({ ...i, index, codiceFiscale: codiciFiscali[i.email?.toLowerCase()] || "" }))
    .filter((i) => {
      const s = ricerca.toLowerCase();
      const testo = [i.firstName, i.lastName, i.email, i.codiceFiscale, ...CAMPI_EXTRA_ISCRITTO.map((c) => i[c.key])];
      return testo.join(" ").toLowerCase().includes(s);
    });

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <TextField placeholder="Cerca iscritto..." size="small" value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Box display="flex" gap={1}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} size="small">
            Importa da XLSX
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleImportaXlsx} />
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={apriNuovo}>
            Aggiungi iscritto
          </Button>
        </Box>
      </Box>

      {esitoImport && (
        <Alert severity={esitoImport.severity} onClose={() => setEsitoImport(null)} sx={{ mb: 2 }}>
          {esitoImport.testo}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell><strong>Cognome</strong></TableCell>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              {mostraCf && <TableCell><strong>Cod. fiscale</strong></TableCell>}
              {colonneExtra.map((c) => <TableCell key={c.key}><strong>{c.label}</strong></TableCell>)}
              <TableCell><strong>Iscritto dal</strong></TableCell>
              <TableCell><strong>Stato</strong></TableCell>
              <TableCell align="center"><strong>Azioni</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrati.map((i) => (
              <TableRow key={i.index} hover>
                <TableCell>{i.lastName || "—"}</TableCell>
                <TableCell>{i.firstName || "—"}</TableCell>
                <TableCell>{i.email}</TableCell>
                {mostraCf && <TableCell>{i.codiceFiscale || "—"}</TableCell>}
                {colonneExtra.map((c) => <TableCell key={c.key}>{i[c.key] || "—"}</TableCell>)}
                <TableCell>{i.dataIscrizione || "—"}</TableCell>
                <TableCell>
                  {i.dataRimozione ? (
                    <Chip size="small" label={`Uscito il ${i.dataRimozione}`} color="default" />
                  ) : (
                    <Chip size="small" label="Attivo" color="success" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => apriModifica(i.index)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {!i.dataRimozione && (
                    <IconButton size="small" color="error" onClick={() => rimuoviRapido(i.index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtrati.length === 0 && (
              <TableRow>
                <TableCell colSpan={6 + colonneExtra.length + (mostraCf ? 1 : 0)} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  Nessun iscritto. Importa un file XLSX o aggiungine uno manualmente.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing?.index === -1 ? "Aggiungi iscritto" : "Modifica iscritto"}</DialogTitle>
        <DialogContent>
          {editing && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField label="Nome" value={editing.firstName}
                onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} required fullWidth />
              <TextField label="Cognome" value={editing.lastName}
                onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} fullWidth />
              <TextField label="Email" type="email" value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })} required fullWidth />
              <TextField label="Codice fiscale" value={editing.codiceFiscale}
                onChange={(e) => setEditing({ ...editing, codiceFiscale: e.target.value })}
                helperText="Visibile solo allo staff" fullWidth />
              {CAMPI_EXTRA_ISCRITTO.map((c) => (
                <TextField key={c.key} label={c.label} value={editing[c.key]}
                  onChange={(e) => setEditing({ ...editing, [c.key]: e.target.value })} fullWidth />
              ))}
              <TextField label="Data iscrizione" type="date" value={editing.dataIscrizione || ""}
                onChange={(e) => setEditing({ ...editing, dataIscrizione: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Data uscita (se non più iscritto)" type="date" value={editing.dataRimozione || ""}
                onChange={(e) => setEditing({ ...editing, dataRimozione: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth />
              {errore && <Alert severity="error">{errore}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={salvaForm} disabled={salvando}>
            {salvando ? <CircularProgress size={20} color="inherit" /> : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ============================= TAB LEZIONI =============================

function TabLezioni({ corso, clientiMap, eventi, quizIndipendenti, presenze, risultatiQuiz, loading }) {
  const { currentUser } = useAuth();
  const [associaOpen, setAssociaOpen] = useState(false);
  const [eventiDisponibili, setEventiDisponibili] = useState([]);
  const [loadingDisponibili, setLoadingDisponibili] = useState(false);
  const [ricercaAssocia, setRicercaAssocia] = useState("");
  const [soloClienteCorso, setSoloClienteCorso] = useState(false);
  const [espansa, setEspansa] = useState(null);
  const [uploadEventoId, setUploadEventoId] = useState(null);
  const [nuovoQuizOpen, setNuovoQuizOpen] = useState(false);
  const [thinkificCourseId, setThinkificCourseId] = useState(corso.thinkificCourseId || "");

  async function salvaThinkificCourseId() {
    const valore = thinkificCourseId.trim();
    if (valore === (corso.thinkificCourseId || "")) return;
    await updateDoc(doc(db, "corsi", corso.id), { thinkificCourseId: valore || null });
  }

  const eventiOrdinati = [...eventi].sort((a, b) =>
    new Date(a.dataInizio || a.data || 0) - new Date(b.dataInizio || b.data || 0)
  );

  function nomeClienteEvento(e) {
    return clientiMap[e.clienteId] || e.clienteNomeStorico || "—";
  }

  // "Stesso cliente" del corso è solo un aiuto per restringere la ricerca: sui dati storici
  // clienteId/clienteNomeStorico sono spesso incoerenti (cliente rinominato, sigle diverse,
  // campo mancante), quindi qui mostriamo TUTTI gli eventi non ancora associati — non solo
  // quelli che "matchano" — per non nascondere lezioni già svolte che vanno comunque trovate.
  async function apriAssociaDialog() {
    setAssociaOpen(true);
    setLoadingDisponibili(true);
    const snap = await getDocs(collection(db, "eventi"));
    const tutti = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const disponibili = tutti
      .filter((e) => !(corso.eventiIds || []).includes(e.id))
      .sort((a, b) => new Date(b.dataInizio || b.data || 0) - new Date(a.dataInizio || a.data || 0));
    setEventiDisponibili(disponibili);
    setLoadingDisponibili(false);
  }

  async function associaEvento(eventoId) {
    const evento = eventiDisponibili.find((e) => e.id === eventoId);
    await updateDoc(doc(db, "corsi", corso.id), { eventiIds: arrayUnion(eventoId) });
    setEventiDisponibili((prev) => prev.filter((e) => e.id !== eventoId));
    salvaLog(currentUser, "Associazione lezione a corso", corso.id, corso.nome, [
      { campo: "Lezione", da: "—", a: evento?.titolo || eventoId }
    ]);
  }

  async function rimuoviLezione(eventoId) {
    if (!window.confirm("Rimuovere questa lezione dal corso? I risultati quiz caricati per questa lezione in questo corso verranno eliminati.")) return;
    const evento = eventiOrdinati.find((e) => e.id === eventoId);
    await updateDoc(doc(db, "corsi", corso.id), { eventiIds: arrayRemove(eventoId) });
    const daEliminare = risultatiQuiz.filter((r) => r.eventoId === eventoId);
    const batch = writeBatch(db);
    daEliminare.forEach((r) => batch.delete(doc(db, "risultatiQuizLezione", r.id)));
    if (daEliminare.length > 0) await batch.commit();
    salvaLog(currentUser, "Rimozione lezione da corso", corso.id, corso.nome, [
      { campo: "Lezione", da: evento?.titolo || eventoId, a: "—" }
    ]);
  }

  async function rimuoviQuizIndipendente(quizId) {
    if (!window.confirm("Rimuovere questo quiz indipendente? I risultati caricati verranno eliminati.")) return;
    const quiz = (quizIndipendenti || []).find((q) => q.id === quizId);
    const daEliminare = risultatiQuiz.filter((r) => r.eventoId === quizId);
    const batch = writeBatch(db);
    daEliminare.forEach((r) => batch.delete(doc(db, "risultatiQuizLezione", r.id)));
    batch.delete(doc(db, "quizIndipendenti", quizId));
    await batch.commit();
    salvaLog(currentUser, "Rimozione quiz indipendente", corso.id, corso.nome, [
      { campo: "Quiz", da: quiz?.nome || quizId, a: "—" }
    ]);
  }

  const quizIndipendentiOrdinati = [...(quizIndipendenti || [])].sort(
    (a, b) => new Date(a.data || 0) - new Date(b.data || 0)
  );

  const nomeClienteCorso = clientiMap[corso.clienteId] || "";

  const eventiFiltrati = eventiDisponibili
    .filter((e) => !soloClienteCorso || nomeClienteEvento(e) === nomeClienteCorso)
    .filter((e) => {
      const s = ricercaAssocia.toLowerCase();
      return `${e.titolo || ""} ${nomeClienteEvento(e)}`.toLowerCase().includes(s);
    });

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <LinkIcon color="action" fontSize="small" />
        <TextField label="ID corso Thinkific" size="small" value={thinkificCourseId}
          onChange={(e) => setThinkificCourseId(e.target.value)}
          onBlur={salvaThinkificCourseId}
          helperText="Collega questo corso al corso su Thinkific per l'import automatico giornaliero dei risultati quiz."
          sx={{ minWidth: 320 }} />
      </Paper>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={apriAssociaDialog}>
          Associa evento esistente
        </Button>
      </Box>

      {eventiOrdinati.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          Nessuna lezione associata. Le lezioni di un corso sono eventi già presenti nel gestionale:
          usa "Associa evento esistente" per collegarli.
        </Typography>
      ) : (
        eventiOrdinati.map((ev) => (
          <RigaLezione key={ev.id} evento={ev}
            presenzeLezione={presenze.filter((p) => p.eventoId === ev.id)}
            quizLezione={risultatiQuiz.filter((r) => r.eventoId === ev.id)}
            espansa={espansa === ev.id} onToggleEspansa={() => setEspansa(espansa === ev.id ? null : ev.id)}
            onRimuovi={() => rimuoviLezione(ev.id)}
            onCaricaTest={() => setUploadEventoId(ev.id)} />
        ))
      )}

      <Box mt={4} mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Quiz indipendenti</Typography>
            <Typography variant="body2" color="text.secondary">
              Risultati di quiz svolti senza una lezione dedicata nel gestionale (es. verifiche generali,
              o test caricati prima che la relativa lezione venisse registrata). Contano nei KPI di quiz
              e progressione, non nella costanza di presenza.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNuovoQuizOpen(true)}>
            Aggiungi quiz indipendente
          </Button>
        </Box>
        {quizIndipendentiOrdinati.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            Nessun quiz indipendente per questo corso.
          </Typography>
        ) : (
          quizIndipendentiOrdinati.map((q) => (
            <RigaLezione key={q.id} standalone
              evento={{ id: q.id, titolo: q.nome, dataInizio: q.data, thinkificQuizId: q.thinkificQuizId }}
              presenzeLezione={[]} quizLezione={risultatiQuiz.filter((r) => r.eventoId === q.id)}
              espansa={espansa === q.id} onToggleEspansa={() => setEspansa(espansa === q.id ? null : q.id)}
              onRimuovi={() => rimuoviQuizIndipendente(q.id)}
              onCaricaTest={() => setUploadEventoId(q.id)} />
          ))
        )}
      </Box>

      <Dialog open={associaOpen} onClose={() => setAssociaOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Associa evento esistente</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Sono mostrati tutti gli eventi del gestionale, comprese le lezioni già svolte in passato.
            Sui dati storici il cliente collegato potrebbe non essere sempre coerente: se non trovi
            una lezione, prova a cercarla per titolo qui sotto.
          </Typography>
          <TextField fullWidth size="small" placeholder="Cerca per titolo o cliente..."
            value={ricercaAssocia} onChange={(e) => setRicercaAssocia(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ mb: 1 }} />
          <FormControlLabel sx={{ mb: 1 }}
            control={<Checkbox size="small" checked={soloClienteCorso}
              onChange={(e) => setSoloClienteCorso(e.target.checked)} />}
            label={`Solo eventi con cliente "${nomeClienteCorso || "—"}"`} />
          {loadingDisponibili ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Titolo</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eventiFiltrati.map((e) => (
                    <TableRow key={e.id} hover sx={{ cursor: "pointer" }} onClick={() => associaEvento(e.id)}>
                      <TableCell>{e.titolo}</TableCell>
                      <TableCell>{nomeClienteEvento(e)}</TableCell>
                      <TableCell>{e.dataInizio || e.data || "—"}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label="Associa" color="primary" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                  {eventiFiltrati.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                        Nessun evento trovato.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssociaOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {uploadEventoId && (
        <DialogCaricaTest corso={corso} eventoId={uploadEventoId} onClose={() => setUploadEventoId(null)} />
      )}
      {nuovoQuizOpen && (
        <DialogNuovoQuizIndipendente corsoId={corso.id} onClose={() => setNuovoQuizOpen(false)} />
      )}
    </Paper>
  );
}

function RigaLezione({ evento, presenzeLezione, quizLezione, espansa, onToggleEspansa, onRimuovi, onCaricaTest, standalone = false }) {
  const [thinkificQuizId, setThinkificQuizId] = useState(evento.thinkificQuizId || "");

  async function salvaThinkificQuizId() {
    const valore = thinkificQuizId.trim();
    if (valore === (evento.thinkificQuizId || "")) return;
    const collezione = standalone ? "quizIndipendenti" : "eventi";
    await updateDoc(doc(db, collezione, evento.id), { thinkificQuizId: valore || null });
  }

  const durataMedia = presenzeLezione.length > 0
    ? Math.round(presenzeLezione.reduce((a, p) => a + (p.durataSecondi ?? p.durataMinuti * 60), 0) / presenzeLezione.length)
    : null;

  const emailQuiz = [...new Set(quizLezione.map((r) => r.email))];
  const mediaQuizPct = emailQuiz.length > 0
    ? emailQuiz.reduce((acc, email) => {
        const righe = quizLezione.filter((r) => r.email === email && r.risposteCorrette != null && r.totaleDomande);
        const migliore = righe.length > 0 ? Math.max(...righe.map((r) => (r.risposteCorrette / r.totaleDomande) * 100)) : null;
        return migliore != null ? acc + migliore : acc;
      }, 0) / emailQuiz.length
    : null;

  // Raggruppa i caricamenti quiz per data caricamento (stesso batch = stesso momento)
  const caricamenti = {};
  quizLezione.forEach((r) => {
    const key = r.dataCaricamento?.seconds ?? "n-d";
    if (!caricamenti[key]) caricamenti[key] = { data: r.dataCaricamento, righe: [] };
    caricamenti[key].righe.push(r);
  });
  const storicoCaricamenti = Object.values(caricamenti).sort((a, b) => (a.data?.seconds || 0) - (b.data?.seconds || 0));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography fontWeight="bold">{evento.titolo}</Typography>
            {standalone && <Chip size="small" label="Quiz indipendente" variant="outlined" />}
          </Box>
          <Typography variant="caption" color="text.secondary">{evento.dataInizio || evento.data || "—"}</Typography>
          <TextField variant="standard" placeholder="ID quiz Thinkific" size="small"
            value={thinkificQuizId} onChange={(e) => setThinkificQuizId(e.target.value)}
            onBlur={salvaThinkificQuizId}
            sx={{ display: "block", mt: 0.5, maxWidth: 200 }}
            InputProps={{ sx: { fontSize: "0.75rem" } }} />
        </Grid>
        <Grid item xs={12} md={3}>
          {standalone ? (
            <Typography variant="body2" color="text.secondary">Nessuna lezione associata</Typography>
          ) : presenzeLezione.length > 0 ? (
            <Typography variant="body2">
              {presenzeLezione.length} presenti · media {secondiInHHMMSS(durataMedia)}
            </Typography>
          ) : (
            <Button size="small" component="a" href={`/evento/${evento.id}/report-presenze`}
              target="_blank" rel="noopener noreferrer">
              Carica presenze
            </Button>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          {emailQuiz.length > 0 ? (
            <Typography variant="body2">
              {emailQuiz.length} risultati · media {Math.round(mediaQuizPct || 0)}%
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">Nessun test caricato</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={2} display="flex" justifyContent="flex-end" gap={0.5}>
          <Button size="small" variant="outlined" onClick={onCaricaTest}>Carica test</Button>
          {storicoCaricamenti.length > 0 && (
            <IconButton size="small" onClick={onToggleEspansa}>
              {espansa ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <IconButton size="small" color="error" onClick={onRimuovi}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={espansa}>
        <Box mt={2}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary">Storico caricamenti test</Typography>
          <Table size="small">
            <TableBody>
              {storicoCaricamenti.map((c, idx) => {
                const valide = c.righe.filter((r) => r.risposteCorrette != null && r.totaleDomande);
                const media = valide.length > 0
                  ? valide.reduce((a, r) => a + (r.risposteCorrette / r.totaleDomande) * 100, 0) / valide.length
                  : null;
                const data = c.data?.toDate ? c.data.toDate().toLocaleString("it-IT") : "—";
                return (
                  <TableRow key={idx}>
                    <TableCell>{data}</TableCell>
                    <TableCell>{c.righe.length} partecipanti</TableCell>
                    <TableCell>{media != null ? `media ${Math.round(media)}%` : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Paper>
  );
}

function DialogCaricaTest({ corso, eventoId, onClose }) {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [totaleDomande, setTotaleDomande] = useState("");
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState(null);

  async function carica() {
    if (!file || !totaleDomande) return;
    setCaricando(true);
    setErrore(null);
    try {
      const record = await leggiJson(file);
      const righe = costruisciRigheQuiz(record, corso.iscritti || [], totaleDomande ? Number(totaleDomande) : null);
      const batch = writeBatch(db);
      righe.forEach((r) => {
        const ref = doc(collection(db, "risultatiQuizLezione"));
        batch.set(ref, { eventoId, corsoId: corso.id, ...r, dataCaricamento: serverTimestamp() });
      });
      await batch.commit();
      salvaLog(currentUser, "Caricamento risultati test corso", corso.id, corso.nome, [
        { campo: "Risultati caricati", da: "—", a: String(righe.length) },
      ]);
      onClose();
    } catch (err) {
      console.error("Errore caricamento test:", err);
      setErrore(err.message || "Errore durante l'elaborazione del file JSON.");
    }
    setCaricando(false);
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Carica risultati test</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Esporta il questionario da Gestione quiz (arriva via mail in CSV), convertilo in JSON su
            csvjson.com e caricalo qui. Puoi ripetere questa operazione più volte per la stessa lezione
            (es. retest a distanza di giorni): ogni caricamento resta salvato separatamente.
          </Typography>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}
            color={file ? "success" : "primary"}>
            {file ? file.name : "Seleziona JSON"}
            <input type="file" accept=".json" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
          </Button>
          <TextField label="Totale domande del quiz" type="number" value={totaleDomande} required
            onChange={(e) => setTotaleDomande(e.target.value)}
            helperText="Obbligatorio: serve per calcolare una percentuale confrontabile tra lezioni diverse." />
          {errore && <Alert severity="error">{errore}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={carica} disabled={!file || !totaleDomande || caricando}>
          {caricando ? <CircularProgress size={20} color="inherit" /> : "Carica"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DialogNuovoQuizIndipendente({ corsoId, onClose }) {
  const { currentUser } = useAuth();
  const [nome, setNome] = useState("");
  const [data, setData] = useState(OGGI());
  const [salvando, setSalvando] = useState(false);

  async function salva() {
    if (!nome.trim()) return;
    setSalvando(true);
    await addDoc(collection(db, "quizIndipendenti"), {
      corsoId, nome: nome.trim(), data, creatoIl: serverTimestamp(),
    });
    salvaLog(currentUser, "Aggiunta quiz indipendente", corsoId, "", [
      { campo: "Quiz", da: "—", a: nome.trim() }
    ]);
    setSalvando(false);
    onClose();
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Nuovo quiz indipendente</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Usa questa opzione per un quiz che non corrisponde a nessuna lezione già registrata nel
            gestionale (es. una verifica generale, o un test svolto prima che la lezione venisse creata).
            Dopo averlo creato potrai caricarci i risultati come per una lezione normale.
          </Typography>
          <TextField label="Nome del quiz" value={nome} onChange={(e) => setNome(e.target.value)}
            required fullWidth autoFocus />
          <TextField label="Data" type="date" value={data} onChange={(e) => setData(e.target.value)}
            InputLabelProps={{ shrink: true }} fullWidth />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={salva} disabled={!nome.trim() || salvando}>
          {salvando ? <CircularProgress size={20} color="inherit" /> : "Crea"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================= TAB KPI STUDENTI =============================

// Colori/etichette per la legenda e lo sfondo delle righe (la classificazione è calcolata da
// statoStudente in kpiCorso.js, condivisa con l'export XLSX per restare coerenti). Lo stato
// "neutro" non compare in legenda: non ha un colore, la riga resta senza evidenziazione.
const COLORI_STATO = {
  bordeaux: { bg: "#A2123E", text: "#fff", label: "Disimpegno totale: costanza, presenza media e quiz svolti tutti a 0" },
  verde: { bg: "#BBF7D0", label: "Punteggio quiz medio superiore al 60%" },
  giallo: { bg: "#FDE68A", label: "Punteggio quiz medio tra il 50% e il 59,9%" },
  rosso: { bg: "#FCA5A5", label: "Punteggio quiz medio sotto il 50%" },
};

const FILTRI_VUOTI = {
  studente: "", costanzaPresenza: "", presenzaMediaPct: "",
  quizSvolti: "", mediaRisposteCorrettePct: "", tentativiTotali: "", trendCorso: "tutti", stato: "tutti",
};
const CAMPI_FILTRO_NUMERICO = ["costanzaPresenza", "presenzaMediaPct", "quizSvolti", "mediaRisposteCorrettePct", "tentativiTotali"];
const ORDINE_TREND = { peggiorato: 0, "n/d": 1, stabile: 2, migliorato: 3 };
const ORDINE_STATO = { verde: 0, neutro: 1, giallo: 2, rosso: 3, bordeaux: 4 };

export function TabKpi({ kpiStudenti, kpiCorsoSerie }) {
  const [studenteSelezionato, setStudenteSelezionato] = useState(null);
  const [filtri, setFiltri] = useState(FILTRI_VUOTI);
  const [sortBy, setSortBy] = useState("studente");
  const [sortDir, setSortDir] = useState("asc");

  function setFiltro(campo, valore) {
    setFiltri((f) => ({ ...f, [campo]: valore }));
  }

  function handleSort(campo) {
    if (sortBy === campo) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(campo); setSortDir("asc"); }
  }

  const filtrati = kpiStudenti.filter((s) => {
    const t = filtri.studente.toLowerCase();
    if (t && !`${s.nome} ${s.cognome} ${s.email}`.toLowerCase().includes(t)) return false;
    if (filtri.trendCorso !== "tutti" && s.trendCorso !== filtri.trendCorso) return false;
    if (filtri.stato !== "tutti" && statoStudente(s) !== filtri.stato) return false;
    for (const campo of CAMPI_FILTRO_NUMERICO) {
      if (filtri[campo] !== "" && Number(s[campo] ?? -Infinity) < Number(filtri[campo])) return false;
    }
    return true;
  });

  function valoreOrdinamento(s, campo) {
    if (campo === "studente") return `${s.cognome} ${s.nome}`.toLowerCase();
    if (campo === "trendCorso") return ORDINE_TREND[s.trendCorso] ?? -1;
    if (campo === "stato") return ORDINE_STATO[statoStudente(s)] ?? -1;
    return s[campo] ?? -Infinity;
  }
  const ordinati = [...filtrati].sort((a, b) => {
    const va = valoreOrdinamento(a, sortBy);
    const vb = valoreOrdinamento(b, sortBy);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // "Lezioni" e "Test" sono conteggi strutturali del corso (quante lezioni/test esistono):
  // non dipendono da quali studenti sono filtrati in tabella, restano fissi.
  const lezioniReali = kpiCorsoSerie.filter((k) => !k.standalone).length;
  const testTotali = kpiCorsoSerie.length;

  // Presenza/punteggio medio invece si ricalcolano sui SOLI studenti che passano i filtri
  // attivi in tabella, come media delle loro medie individuali (già confrontabili tra loro
  // per come sono calcolate in calcolaKpiStudenti) — così le card riflettono il sottoinsieme
  // effettivamente visibile, non sempre l'intero corso.
  const presenzeFiltrate = filtrati.map((s) => s.presenzaMediaPct).filter((v) => v != null);
  const punteggiFiltrati = filtrati.map((s) => s.mediaRisposteCorrettePct).filter((v) => v != null);
  const mediaPresenzaCorso = presenzeFiltrate.length > 0 ? presenzeFiltrate.reduce((a, b) => a + b, 0) / presenzeFiltrate.length : null;
  const mediaPunteggioCorso = punteggiFiltrati.length > 0 ? punteggiFiltrati.reduce((a, b) => a + b, 0) / punteggiFiltrati.length : null;

  // Andamento per lezione ricalcolato sui soli studenti filtrati, stessa logica delle card:
  // per ogni lezione si prendono le voci di andamentoLezioni (già calcolate per studente) di
  // chi è pertinente a quella lezione tra i filtrati, e se ne fa la media — un quiz non svolto
  // vale 0 solo se per quella lezione esiste un punteggio medio corso (cioè il quiz era
  // disponibile), un'assenza vale 0 solo se la presenza media corso per quella lezione è
  // calcolabile (durata di riferimento nota): stessi criteri già usati a livello di corso.
  const datiGrafico = kpiCorsoSerie.map((k, idx) => {
    const vociPertinenti = filtrati
      .map((s) => s.andamentoLezioni.find((l) => l.evento.id === k.eventoId))
      .filter((l) => l != null);

    let presenzaFiltrata = null;
    if (!k.standalone && k.presenzaMediaPct != null && vociPertinenti.length > 0) {
      const valori = vociPertinenti.map((l) => l.presenzaPct).filter((v) => v != null);
      if (valori.length > 0) presenzaFiltrata = valori.reduce((a, b) => a + b, 0) / valori.length;
    }

    let punteggioFiltrato = null;
    if (k.punteggioMedioPct != null && vociPertinenti.length > 0) {
      const valori = vociPertinenti.map((l) => l.punteggioMigliore ?? 0);
      punteggioFiltrato = valori.reduce((a, b) => a + b, 0) / valori.length;
    }

    return {
      nome: k.titolo || `Lezione ${idx + 1}`,
      Presenza: presenzaFiltrata != null ? Math.round(presenzaFiltrata) : null,
      Punteggio: punteggioFiltrato != null ? Math.round(punteggioFiltrato) : null,
    };
  });

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#E6F1FB" }}>
            <Typography variant="h3" fontWeight="bold" color="primary">{lezioniReali}</Typography>
            <Typography variant="body2" color="text.secondary">Lezioni</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#F3E8FD" }}>
            <Typography variant="h3" fontWeight="bold" sx={{ color: "#7C3AED" }}>{testTotali}</Typography>
            <Typography variant="body2" color="text.secondary">Test (lezioni + quiz indipendenti)</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#EAF3DE" }}>
            <Typography variant="h3" fontWeight="bold" color="success.main">{arrotonda(mediaPresenzaCorso)}</Typography>
            <Typography variant="body2" color="text.secondary">Presenza media ({filtrati.length} studenti)</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#FAEEDA" }}>
            <Typography variant="h3" fontWeight="bold" color="warning.main">{arrotonda(mediaPunteggioCorso)}</Typography>
            <Typography variant="body2" color="text.secondary">Punteggio medio ({filtrati.length} studenti)</Typography>
          </Paper>
        </Grid>
      </Grid>

      {datiGrafico.length > 1 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Andamento del corso</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={datiGrafico}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} unit="%" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Presenza" stroke="#1A56A0" strokeWidth={2} connectNulls />
              <Line type="monotone" dataKey="Punteggio" stroke="#F59E0B" strokeWidth={2} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      <Paper elevation={1} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">KPI per studente</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={`${ordinati.length} studenti`} size="small" />
            {JSON.stringify(filtri) !== JSON.stringify(FILTRI_VUOTI) && (
              <Button size="small" onClick={() => setFiltri(FILTRI_VUOTI)}>Azzera filtri</Button>
            )}
          </Box>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          {Object.entries(COLORI_STATO).map(([chiave, { bg, label }]) => (
            <Box key={chiave} display="flex" alignItems="center" gap={0.75}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: bg, border: "1px solid rgba(0,0,0,0.15)" }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                <TableCell>
                  <TableSortLabel active={sortBy === "studente"} direction={sortBy === "studente" ? sortDir : "asc"}
                    onClick={() => handleSort("studente")}><strong>Studente</strong></TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <MuiTooltip title="Numero di lezioni già svolte a cui lo studente ha partecipato, sul totale delle lezioni svolte finora">
                    <TableSortLabel active={sortBy === "costanzaPresenza"} direction={sortBy === "costanzaPresenza" ? sortDir : "asc"}
                      onClick={() => handleSort("costanzaPresenza")}><strong>Costanza</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="right">
                  <MuiTooltip title="Percentuale media della durata seguita sul totale delle lezioni svolte finora: le lezioni non seguite contano 0%, così è confrontabile tra studenti diversi">
                    <TableSortLabel active={sortBy === "presenzaMediaPct"} direction={sortBy === "presenzaMediaPct" ? sortDir : "asc"}
                      onClick={() => handleSort("presenzaMediaPct")}><strong>Presenza media</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="right">
                  <MuiTooltip title="Numero di quiz svolti dallo studente sul totale dei quiz disponibili nel corso finora">
                    <TableSortLabel active={sortBy === "quizSvolti"} direction={sortBy === "quizSvolti" ? sortDir : "asc"}
                      onClick={() => handleSort("quizSvolti")}><strong>Quiz svolti</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="right">
                  <MuiTooltip title="Percentuale media del punteggio migliore ottenuto, calcolata sul totale dei quiz disponibili nel corso: un quiz non svolto conta 0%, così è confrontabile tra studenti diversi">
                    <TableSortLabel active={sortBy === "mediaRisposteCorrettePct"} direction={sortBy === "mediaRisposteCorrettePct" ? sortDir : "asc"}
                      onClick={() => handleSort("mediaRisposteCorrettePct")}><strong>Punteggio medio</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="right">
                  <MuiTooltip title="Numero totale di tentativi di quiz effettuati, sommando anche i quiz ripetuti più volte sulla stessa lezione">
                    <TableSortLabel active={sortBy === "tentativiTotali"} direction={sortBy === "tentativiTotali" ? sortDir : "asc"}
                      onClick={() => handleSort("tentativiTotali")}><strong>Tentativi</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="center">
                  <MuiTooltip title="Andamento del punteggio quiz tra la prima e l'ultima lezione con un risultato registrato">
                    <TableSortLabel active={sortBy === "trendCorso"} direction={sortBy === "trendCorso" ? sortDir : "asc"}
                      onClick={() => handleSort("trendCorso")}><strong>Trend</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
                <TableCell align="center">
                  <MuiTooltip title="Verde: punteggio quiz medio > 60%. Giallo: punteggio quiz medio tra 50% e 59,9%. Rosso: punteggio quiz medio < 50%. Bordeaux: costanza, presenza media e quiz svolti tutti a 0.">
                    <TableSortLabel active={sortBy === "stato"} direction={sortBy === "stato" ? sortDir : "asc"}
                      onClick={() => handleSort("stato")}><strong>Stato</strong></TableSortLabel>
                  </MuiTooltip>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                <TableCell>
                  <TextField variant="standard" size="small" fullWidth placeholder="Filtra nome/email..."
                    value={filtri.studente} onChange={(e) => setFiltro("studente", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                </TableCell>
                <TableCell align="right">
                  <TextField variant="standard" size="small" type="number" placeholder="min %" sx={{ width: 64 }}
                    value={filtri.costanzaPresenza} onChange={(e) => setFiltro("costanzaPresenza", e.target.value)} />
                </TableCell>
                <TableCell align="right">
                  <TextField variant="standard" size="small" type="number" placeholder="min %" sx={{ width: 64 }}
                    value={filtri.presenzaMediaPct} onChange={(e) => setFiltro("presenzaMediaPct", e.target.value)} />
                </TableCell>
                <TableCell align="right">
                  <TextField variant="standard" size="small" type="number" placeholder="min" sx={{ width: 56 }}
                    value={filtri.quizSvolti} onChange={(e) => setFiltro("quizSvolti", e.target.value)} />
                </TableCell>
                <TableCell align="right">
                  <TextField variant="standard" size="small" type="number" placeholder="min %" sx={{ width: 64 }}
                    value={filtri.mediaRisposteCorrettePct} onChange={(e) => setFiltro("mediaRisposteCorrettePct", e.target.value)} />
                </TableCell>
                <TableCell align="right">
                  <TextField variant="standard" size="small" type="number" placeholder="min" sx={{ width: 56 }}
                    value={filtri.tentativiTotali} onChange={(e) => setFiltro("tentativiTotali", e.target.value)} />
                </TableCell>
                <TableCell align="center">
                  <FormControl variant="standard" size="small" sx={{ minWidth: 120 }}>
                    <Select value={filtri.trendCorso} onChange={(e) => setFiltro("trendCorso", e.target.value)}>
                      <MenuItem value="tutti">Tutti</MenuItem>
                      <MenuItem value="migliorato">In miglioramento</MenuItem>
                      <MenuItem value="stabile">Stabile</MenuItem>
                      <MenuItem value="peggiorato">In calo</MenuItem>
                      <MenuItem value="n/d">N/D</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <FormControl variant="standard" size="small" sx={{ minWidth: 90 }}>
                    <Select value={filtri.stato} onChange={(e) => setFiltro("stato", e.target.value)}>
                      <MenuItem value="tutti">Tutti</MenuItem>
                      <MenuItem value="verde">Verde</MenuItem>
                      <MenuItem value="giallo">Giallo</MenuItem>
                      <MenuItem value="rosso">Rosso</MenuItem>
                      <MenuItem value="bordeaux">Bordeaux</MenuItem>
                      <MenuItem value="neutro">Nessuna evidenziazione</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordinati.map((s) => {
                const stato = statoStudente(s);
                const stile = COLORI_STATO[stato];
                return (
                  <TableRow key={s.email} hover
                    sx={{
                      cursor: "pointer",
                      backgroundColor: stile?.bg,
                      ...(stile?.text ? { "& .MuiTableCell-root": { color: stile.text } } : {}),
                    }}
                    onClick={() => setStudenteSelezionato(s)}>
                    <TableCell>
                      {s.nome} {s.cognome}
                      {!s.attivo && <Chip size="small" label="uscito" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right">{formatCostanza(s)}</TableCell>
                    <TableCell align="right">{arrotonda(s.presenzaMediaPct)}</TableCell>
                    <TableCell align="right">{formatQuizSvolti(s)}</TableCell>
                    <TableCell align="right">{arrotonda(s.mediaRisposteCorrettePct)}</TableCell>
                    <TableCell align="right">{s.tentativiTotali}</TableCell>
                    <TableCell align="center">
                      <MuiTooltip title={LABEL_TREND[s.trendCorso] || s.trendCorso}>
                        <span><TrendIcon trend={s.trendCorso} /></span>
                      </MuiTooltip>
                    </TableCell>
                    <TableCell align="center">
                      {stile ? (
                        <MuiTooltip title={stile.label}>
                          <Chip size="small" label={stato}
                            sx={{ backgroundColor: stile.bg, color: stile.text, textTransform: "capitalize" }} />
                        </MuiTooltip>
                      ) : (
                        <MuiTooltip title="Nessuna evidenziazione particolare">
                          <Chip size="small" label="—" variant="outlined" />
                        </MuiTooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {ordinati.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                    Nessuno studente trovato con i filtri attuali.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Drawer anchor="right" open={Boolean(studenteSelezionato)} onClose={() => setStudenteSelezionato(null)}>
        {studenteSelezionato && <DettaglioStudente studente={studenteSelezionato} />}
      </Drawer>
    </Box>
  );
}

function DettaglioStudente({ studente }) {
  const datiPresenza = studente.andamentoLezioni.map((l, idx) => ({
    nome: l.evento?.titolo?.slice(0, 14) || `L${idx + 1}`,
    Presenza: l.presenzaPct != null ? Math.round(l.presenzaPct) : 0,
  }));
  const datiPunteggio = studente.andamentoLezioni
    .filter((l) => l.punteggioMigliore != null)
    .map((l, idx) => ({
      nome: l.evento?.titolo?.slice(0, 14) || `L${idx + 1}`,
      Punteggio: Math.round(l.punteggioMigliore),
    }));

  return (
    <Box sx={{ width: 480, p: 3 }}>
      <Typography variant="h6" fontWeight="bold">{studente.nome} {studente.cognome}</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>{studente.email}</Typography>

      <Grid container spacing={1} mb={3}>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              {studente.lezioniTotali ? `${studente.lezioniFrequentate}/${studente.lezioniTotali}` : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Costanza {studente.lezioniTotali ? `(${arrotonda(studente.costanzaPresenza)})` : ""}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: "center" }}>
            <Typography variant="h6" fontWeight="bold">{arrotonda(studente.mediaRisposteCorrettePct)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Punteggio medio {studente.quizTotali ? `(${studente.quizSvolti}/${studente.quizTotali} quiz)` : ""}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {datiPunteggio.length > 1 && (
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>Punteggio per lezione</Typography>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={datiPunteggio}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} unit="%" />
              <Tooltip />
              <Line type="monotone" dataKey="Punteggio" stroke="#F59E0B" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      {datiPresenza.length > 1 && (
        <Box mb={3}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>Presenza per lezione</Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={datiPresenza}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nome" tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} unit="%" />
              <Tooltip />
              <Bar dataKey="Presenza" fill="#1A56A0" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Typography variant="subtitle2" fontWeight="bold" mb={1}>Andamento per lezione</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Lezione</TableCell>
            <TableCell align="right">Presenza</TableCell>
            <TableCell align="right">Punteggio</TableCell>
            <TableCell align="center">Trend</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {studente.andamentoLezioni.map((l, idx) => (
            <TableRow key={idx}>
              <TableCell>{l.evento?.titolo || "—"}</TableCell>
              <TableCell align="right">{arrotonda(l.presenzaPct)}</TableCell>
              <TableCell align="right">
                {l.serie.length > 1
                  ? l.serie.map((s) => Math.round(s.punteggioPct)).join(" → ") + "%"
                  : arrotonda(l.punteggioMigliore)}
              </TableCell>
              <TableCell align="center">
                <MuiTooltip title={LABEL_TREND[l.trendLezione] || l.trendLezione}>
                  <span><TrendIcon trend={l.trendLezione} /></span>
                </MuiTooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ============================= TAB REFERENTI =============================

function TabReferenti({ corsoId }) {
  const [copiato, setCopiato] = useState(false);
  const linkCliente = `${window.location.origin}/accesso-corso/${corsoId}`;

  function copiaLink() {
    navigator.clipboard.writeText(linkCliente).then(() => {
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    });
  }

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={1}>Link di accesso cliente</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Condividi questo link con i referenti aggiunti qui sotto: potranno accedere con OTP via email.
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField value={linkCliente} InputProps={{ readOnly: true, startAdornment: <LinkIcon sx={{ mr: 1 }} color="action" /> }}
            fullWidth size="small" />
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copiaLink}>
            {copiato ? "Copiato!" : "Copia"}
          </Button>
        </Box>
      </Paper>
      <Paper elevation={1} sx={{ p: 3 }}>
        <ReferentiCorso corsoId={corsoId} />
      </Paper>
    </Box>
  );
}
